import { create } from 'zustand';
import {
  defaultEditState,
  normalizeEditState,
  type EditState,
} from '../editor/editState';

interface EditorStore {
  state: EditState;
  past: EditState[];
  future: EditState[];
  dirty: boolean;

  load: (raw: Partial<EditState> | undefined) => void;
  // Apply a mutation and push the previous state onto the undo stack.
  update: (mutator: (draft: EditState) => void) => void;
  set: <K extends keyof EditState>(key: K, value: EditState[K]) => void;
  reset: () => void;
  undo: () => void;
  redo: () => void;
  markSaved: () => void;
}

const clone = (s: EditState): EditState =>
  typeof structuredClone === 'function'
    ? structuredClone(s)
    : JSON.parse(JSON.stringify(s));

export const useEditorStore = create<EditorStore>((set) => ({
  state: defaultEditState(),
  past: [],
  future: [],
  dirty: false,

  load: (raw) =>
    set({
      state: normalizeEditState(raw),
      past: [],
      future: [],
      dirty: false,
    }),

  update: (mutator) =>
    set((s) => {
      const next = clone(s.state);
      mutator(next);
      return {
        state: next,
        past: [...s.past.slice(-49), s.state],
        future: [],
        dirty: true,
      };
    }),

  set: (key, value) =>
    set((s) => {
      const next = clone(s.state);
      next[key] = value;
      return {
        state: next,
        past: [...s.past.slice(-49), s.state],
        future: [],
        dirty: true,
      };
    }),

  reset: () =>
    set((s) => ({
      state: defaultEditState(),
      past: [...s.past.slice(-49), s.state],
      future: [],
      dirty: true,
    })),

  undo: () =>
    set((s) => {
      if (!s.past.length) return s;
      const previous = s.past[s.past.length - 1];
      return {
        state: previous,
        past: s.past.slice(0, -1),
        future: [s.state, ...s.future],
        dirty: true,
      };
    }),

  redo: () =>
    set((s) => {
      if (!s.future.length) return s;
      const next = s.future[0];
      return {
        state: next,
        past: [...s.past, s.state],
        future: s.future.slice(1),
        dirty: true,
      };
    }),

  markSaved: () => set({ dirty: false }),
}));
